import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { MessageSquare, FileText } from "lucide-react";

/**
 * Core communications fields: title, body, type_id, type_label, direction.
 *
 * Each field is self-contained — display config, optional GraphQL hint, and
 * `savedViewMembership` listing which saved views include it by default.
 * `./communications-saved-views.ts` derives `visibleColumns` from these
 * memberships via `collectFieldsForSavedView`.
 */
export const coreCommunicationsFields = defineFieldConfigMap({
  // FIELD: title
  title: {
    type: "text",
    label: "Communication Title",
    placeholder: "Communication title...",
    required: true,
    validation: {
      required: "Communication title is required.",
    },
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "contains",
    width: 300,
    minWidth: 200,
    maxWidth: 500,
    icon: "MessageSquare",
    mobileDetailSectionId: "details",
    // Role-based access control - visible to all roles except client_user
    allowedRoles: ["wtc_employee", "wtc_admin"],
    // Show row actions inline with this column
    showRowActions: true,
    showOnCard: true,
    cardSpan: 2,
    graphqlSelector: "title",
    savedViewMembership: [
      "all-communications",
      "recent-communications",
      "type-email",
      "type-note",
      "type-call",
      "type-meeting",
      "outbound-communications",
      "inbound-communications",
    ],
  },
  // FIELD: body
  body: {
    type: "textarea",
    label: "Description",
    placeholder: "Describe the communication...",
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 250,
    minWidth: 150,
    maxWidth: 400,
    icon: "FileText",
    mobileDetailSectionId: "details",
    // Role-based access control
    allowedRoles: ["wtc_employee", "wtc_admin"],
  },
  // FIELD: type_id
  type_id: {
    type: "select",
    label: "Communication Type",
    formSpan: 1,
    isForeignKey: true,
    foreignKey: {
      referencedTable: "communication_types",
      referencedColumn: "id",
      constraintName: "communications_type_id_fkey",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Select communication type...",
    // NOTE: Options are fetched dynamically from communication_types table
    // The form component should populate these options by querying the database
    // Labels are displayed to users, but UUIDs (id field) are passed to backend
    options: [],
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 150,
    minWidth: 120,
    maxWidth: 200,
    icon: "Tag",
    mobileDetailSectionId: "details",
    graphqlSelector: "type_id",
    savedViewMembership: [
      "all-communications",
      "recent-communications",
      "outbound-communications",
      "inbound-communications",
    ],
  },
  // FIELD: type_label
  type_label: {
    type: "text",
    label: "Type",
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: false,
    excludeFromApi: true,
    filterType: "contains",
    width: 150,
    minWidth: 120,
    maxWidth: 200,
    icon: "Tag",
    mobileDetailSectionId: "details",
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
  },
  // FIELD: direction
  direction: {
    type: "select",
    label: "Direction",
    formSpan: 1,
    options: [
      { label: "Inbound", value: "inbound" },
      { label: "Outbound", value: "outbound" },
    ],
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "CheckCircle",
    mobileDetailSectionId: "details",
    groupable: true,
    graphqlSelector: "direction",
    savedViewMembership: [
      "all-communications",
      "recent-communications",
      "type-email",
      "type-note",
      "type-call",
      "type-meeting",
      "outbound-communications",
      "inbound-communications",
    ],
  },
});
